import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useIdentity } from "../lib/identity";
import { Building2 } from "lucide-react";

interface NavLinkDef {
  label: string;
  to: string;
  platformAdminOnly?: boolean;
  portalAdminOnly?: boolean;
  adminOnly?: boolean;
}

const TOP_NAV: NavLinkDef[] = [
  { label: "Dashboard", to: "/dashboard" },
  { label: "Certifications", to: "/certifications" },
  { label: "My Programs", to: "/programs" },
  { label: "Badges", to: "/badges" },
  { label: "Seats", to: "/seats", adminOnly: true },
  { label: "Renewals", to: "/renewals" },
  { label: "Admin", to: "/admin", platformAdminOnly: true },
];

const ALL_SIDEBAR_LINKS: NavLinkDef[] = [
  { label: "Overview", to: "/dashboard" },
  { label: "Certification Catalog", to: "/certifications" },
  { label: "My Programs", to: "/programs" },
  { label: "Certificates", to: "/certificates" },
  { label: "Digital Badges", to: "/badges" },
  { label: "Capstone Simulations", to: "/capstones" },
  { label: "Renewals", to: "/renewals" },
  { label: "Licenses", to: "/licenses" },
  { label: "Seat Manager", to: "/seats", adminOnly: true },
  { label: "Account Settings", to: "/account" },
  { label: "Portal Management", to: "/portal/manage", portalAdminOnly: true },
  { label: "Admin Console", to: "/admin", platformAdminOnly: true },
];

function isAdminRole(role: string): boolean {
  return role === "agency_admin" || role === "enterprise_admin" || role === "platform_admin";
}

function isPortalAdmin(role: string): boolean {
  return role === "enterprise_admin" || role === "agency_admin" || role === "platform_admin";
}

function isPlatformAdmin(role: string): boolean {
  return role === "platform_admin";
}

export function PlatformLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const user = { name: identity.name ?? "Guest", role: identity.roles[0] ?? "student" };

  const topNav = TOP_NAV.filter((link) => {
    if (link.platformAdminOnly && !isPlatformAdmin(user.role)) return false;
    if (link.adminOnly && !isAdminRole(user.role)) return false;
    return true;
  });

  const sidebarLinks = ALL_SIDEBAR_LINKS.filter((link) => {
    if (link.platformAdminOnly && !isPlatformAdmin(user.role)) return false;
    if (link.portalAdminOnly && !isPortalAdmin(user.role)) return false;
    if (link.adminOnly && !isAdminRole(user.role)) return false;
    return true;
  });

  async function handleLogout() {
    navigate("/logout");
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-subtle)" }}>
      {/* Top navigation bar */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(10, 22, 40, 0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "var(--nav-height)",
            maxWidth: "var(--max-width)",
            margin: "0 auto",
            padding: "0 var(--space-4)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: "0.625rem", flexShrink: 0 }}>
              <img src="/sentinel-white.png" alt="Sentinel" style={{ width: "30px", height: "30px", objectFit: "contain" }} />
              <span style={{ fontWeight: 800, fontSize: "1.125rem", color: "var(--text-on-dark)", letterSpacing: "-0.01em" }}>
                Sentinel
              </span>
            </Link>
            <button
              className="platform-sidebar-toggle"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Toggle sidebar"
              style={{ display: "none", background: "transparent", border: "none", color: "var(--text-on-dark)", fontSize: "1.25rem", cursor: "pointer" }}
            >
              &#9776;
            </button>
          </div>

          {/* Desktop top nav */}
          <div className="platform-topnav" style={{ display: "flex", alignItems: "center", gap: "0.25rem", flex: 1, justifyContent: "center" }}>
            {topNav.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                style={({ isActive }) => ({
                  padding: "0.5rem 0.875rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: isActive ? "var(--accent-400)" : "var(--text-muted-on-dark)",
                  borderRadius: "var(--radius-md)",
                  transition: "color var(--transition-fast)",
                  whiteSpace: "nowrap",
                })}
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* User menu */}
          <div className="platform-usermenu" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
            {/* Company logo (if user belongs to an org) */}
            {identity.organizationLogoUrl ? (
              <img
                src={identity.organizationLogoUrl}
                alt={identity.organizationName ?? "Company"}
                title={identity.organizationName ?? "Company"}
                style={{ width: "36px", height: "36px", objectFit: "contain", borderRadius: "var(--radius-md)", background: "#fff", padding: "0.125rem", flexShrink: 0 }}
              />
            ) : identity.organizationName ? (
              <div
                title={identity.organizationName}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "var(--radius-md)",
                  background: "rgba(255,255,255,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-on-dark)",
                  flexShrink: 0,
                }}
              >
                <Building2 size={18} />
              </div>
            ) : null}

            <div style={{ textAlign: "right", lineHeight: 1.3 }}>
              <Link to="/account" style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-on-dark)", textDecoration: "none" }}>{user.name}</Link>
              <div style={{ fontSize: "0.6875rem", color: "var(--text-muted-on-dark)", textTransform: "capitalize" }}>{user.role.replace("_", " ")}</div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "var(--radius-md)",
                padding: "0.375rem 0.75rem",
                color: "var(--text-muted-on-dark)",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Log out
            </button>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--accent-500), var(--primary-600))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.875rem",
                flexShrink: 0,
              }}
            >
              {user.name.split(" ").map((n) => n[0]).join("")}
            </div>
          </div>
        </nav>

        {/* Mobile top nav */}
        <div className="platform-mobile-topnav" style={{ display: "none", padding: "0 var(--space-4) 0.75rem", gap: "0.25rem", overflowX: "auto" }}>
          {topNav.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              style={({ isActive }) => ({
                padding: "0.4rem 0.625rem",
                fontSize: "0.75rem",
                fontWeight: 500,
                color: isActive ? "var(--accent-400)" : "var(--text-muted-on-dark)",
                whiteSpace: "nowrap",
                flexShrink: 0,
              })}
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <style>{`
          @media (max-width: 1024px) {
            .platform-topnav { display: none !important; }
            .platform-mobile-topnav { display: flex !important; }
          }
          @media (max-width: 768px) {
            .platform-usermenu div:first-child { display: none; }
            .platform-sidebar-toggle { display: block !important; }
          }
        `}</style>
      </header>

      {/* Body: sidebar + main */}
      <div style={{ maxWidth: "var(--max-width)", margin: "0 auto", display: "flex", gap: 0, minHeight: "calc(100vh - var(--nav-height))" }}>
        {/* Sidebar */}
        <aside
          className="platform-sidebar"
          style={{
            width: "240px",
            flexShrink: 0,
            padding: "var(--space-4) var(--space-2)",
            borderRight: "1px solid var(--border)",
            background: "var(--surface)",
          }}
        >
          <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)", marginBottom: "0.75rem", paddingLeft: "0.5rem" }}>
            Quick Links
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
            {sidebarLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                style={({ isActive }) => ({
                  padding: "0.625rem 0.75rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: isActive ? "var(--accent-600)" : "var(--text)",
                  background: isActive ? "var(--accent-50)" : "transparent",
                  borderRadius: "var(--radius-md)",
                  border: isActive ? "1px solid var(--accent-100)" : "1px solid transparent",
                  transition: "all var(--transition-fast)",
                })}
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <div style={{ marginTop: "var(--space-5)", padding: "var(--space-3)", borderRadius: "var(--radius-lg)", background: "var(--bg-subtle)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, marginBottom: "0.375rem", color: "var(--text)" }}>Need Help?</div>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Contact our certification support team.</p>
            <a href="mailto:support@e-industries.com" className="btn btn-secondary" style={{ width: "100%", padding: "0.375rem 0.625rem", fontSize: "0.75rem" }}>
              Contact Support
            </a>
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: "fixed",
              inset: "var(--nav-height) 0 0 0",
              background: "rgba(0,0,0,0.4)",
              zIndex: 90,
            }}
          >
            <aside
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: "260px",
                background: "var(--surface)",
                padding: "var(--space-4)",
                borderRight: "1px solid var(--border)",
                overflowY: "auto",
              }}
            >
              <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)", marginBottom: "0.75rem" }}>
                Quick Links
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                {sidebarLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setSidebarOpen(false)}
                    style={{
                      padding: "0.625rem 0.75rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: location.pathname === link.to ? "var(--accent-600)" : "var(--text)",
                      background: location.pathname === link.to ? "var(--accent-50)" : "transparent",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main style={{ flex: 1, padding: "var(--space-5) var(--space-4)", minWidth: 0 }}>
          {children}
        </main>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .platform-sidebar { display: none !important; }
        }
      `}</style>
    </div>
  );
}
